// Module-level store for per-conversation chat-turn streaming state.
//
// Why this file exists:
//   - When the user switches to another conversation mid-stream, Chat.tsx
//     unmounts and the local SSE reader is dropped — losing the live tokens
//     for the original conversation. By hoisting the stream into module state
//     keyed by convId, navigation no longer kills the stream, and AppShell
//     can render a per-conversation "running" spinner.
//
// Public surface:
//   - startTurn(token, convId, content, options?) → kicks off a stream; safe to call
//     once per conversation, idempotent while busy.
//   - useStreamState(convId)   → hook for Chat.tsx
//   - useBusyConvIds()         → hook for AppShell sidebar spinners
//   - dismissToast(convId)     → manually clear the "memory_learned" toast

import { useCallback, useSyncExternalStore } from "react";
import { ApiError, streamChatTurn, type ChatEvent, type ChatTurnRequestOptions } from "./api";
import {
  EVENT_CONVERSATIONS_CHANGED,
  EVENT_PENDING_RULESET_EDIT,
  EVENT_WORKFLOWS_CHANGED,
} from "./workflowConstants";

export interface InflightToolCall {
  callId: string;
  tool: string;
  args: Record<string, unknown>;
  outcome: "running" | "success" | "error";
  errorCode: string | null;
  durationMs: number | null;
  preview: string | null;
}

export interface InflightTurn {
  reasoning: string;
  tokens: string;
  toolCalls: InflightToolCall[];
  done: boolean;
  error: { code: string; message: string } | null;
}

const EMPTY_TURN: InflightTurn = {
  reasoning: "",
  tokens: "",
  toolCalls: [],
  done: false,
  error: null,
};

export interface StreamState {
  busy: boolean;
  inflight: InflightTurn | null;
  toast: string | null;
  // monotonic counter — Chat.tsx watches this to reload persisted history
  reloadTick: number;
}

const EMPTY_STATE: StreamState = {
  busy: false,
  inflight: null,
  toast: null,
  reloadTick: 0,
};

function parseToolPreview(raw: string | null | undefined): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

const states = new Map<string, StreamState>();
const perConvListeners = new Map<string, Set<() => void>>();
const busyListeners = new Set<() => void>();
// Active AbortController per conversation — populated for the duration of
// a running stream, deleted after the stream resolves or aborts.
const abortControllers = new Map<string, AbortController>();

let busySnapshot: ReadonlySet<string> = new Set();

function getState(convId: string): StreamState {
  return states.get(convId) ?? EMPTY_STATE;
}

function setState(convId: string, mutator: (prev: StreamState) => StreamState): void {
  const prev = states.get(convId) ?? EMPTY_STATE;
  const next = mutator(prev);
  if (next === prev) return;
  states.set(convId, next);
  // notify per-conv subscribers
  const ls = perConvListeners.get(convId);
  if (ls) for (const l of ls) l();
  // recompute busy snapshot only when the busy flag actually changed
  if (prev.busy !== next.busy) recomputeBusy();
}

function recomputeBusy(): void {
  const next = new Set<string>();
  for (const [id, s] of states) if (s.busy) next.add(id);
  // shallow set-equality
  if (next.size === busySnapshot.size) {
    let same = true;
    for (const id of next) if (!busySnapshot.has(id)) { same = false; break; }
    if (same) return;
  }
  busySnapshot = next;
  for (const l of busyListeners) l();
}

// ---- public read API ----------------------------------------------------

export function getStreamState(convId: string): StreamState {
  return getState(convId);
}

export function getBusyConvIds(): ReadonlySet<string> {
  return busySnapshot;
}

export function clearInflight(convId: string): void {
  setState(convId, (p) => (p.inflight ? { ...p, inflight: null } : p));
}

export function dismissToast(convId: string): void {
  setState(convId, (p) => (p.toast ? { ...p, toast: null } : p));
}

// ---- subscribe helpers (consumed by useSyncExternalStore) ---------------

function subscribePerConv(convId: string, fn: () => void): () => void {
  let bucket = perConvListeners.get(convId);
  if (!bucket) {
    bucket = new Set();
    perConvListeners.set(convId, bucket);
  }
  bucket.add(fn);
  return () => {
    bucket!.delete(fn);
    if (bucket!.size === 0) perConvListeners.delete(convId);
  };
}

function subscribeBusy(fn: () => void): () => void {
  busyListeners.add(fn);
  return () => {
    busyListeners.delete(fn);
  };
}

// ---- React hooks --------------------------------------------------------

export function useStreamState(convId: string | undefined | null): StreamState {
  const subscribe = useCallback(
    (fn: () => void) => (convId ? subscribePerConv(convId, fn) : () => {}),
    [convId],
  );
  const snapshot = useCallback(() => (convId ? getState(convId) : EMPTY_STATE), [convId]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export function useBusyConvIds(): ReadonlySet<string> {
  return useSyncExternalStore(subscribeBusy, getBusyConvIds, getBusyConvIds);
}

// ---- the actual stream driver ------------------------------------------

const TOAST_MS = 4000;

export function stopTurn(convId: string): void {
  const ctrl = abortControllers.get(convId);
  if (!ctrl) return;
  ctrl.abort();
}

export async function startTurn(
  token: string,
  convId: string,
  content: string,
  options: ChatTurnRequestOptions = {},
): Promise<void> {
  // Guard: ignore if a turn is already running for this conv
  if (getState(convId).busy) return;

  const ctrl = new AbortController();
  abortControllers.set(convId, ctrl);

  setState(convId, (p) => ({
    busy: true,
    inflight: { ...EMPTY_TURN },
    toast: null,
    reloadTick: p.reloadTick,
  }));

  let messageDoneSeen = false;
  let conversationChangedEmitted = false;
  const emitConversationChanged = (): void => {
    if (conversationChangedEmitted) return;
    conversationChangedEmitted = true;
    window.dispatchEvent(new CustomEvent(EVENT_CONVERSATIONS_CHANGED));
  };

  try {
    await streamChatTurn(token, convId, content, (e: ChatEvent) => {
      // Out-of-band: memory_learned toast
      if (e.type === "memory_learned") {
        const msg = `已学到 ${e.data.count} 条新知识`;
        setState(convId, (p) => ({ ...p, toast: msg }));
        window.setTimeout(() => {
          setState(convId, (p) => (p.toast === msg ? { ...p, toast: null } : p));
        }, TOAST_MS);
        return;
      }
      // Early-finalize on message_done: flip busy off, bump reloadTick so
      // Chat.tsx refetches the persisted history; the stream may still emit
      // trailing memory_learned + close.
      if (e.type === "message_done" && !messageDoneSeen) {
        messageDoneSeen = true;
        // 保留 inflight 气泡直到 Chat.tsx 把持久化历史拉回来(clearInflight),
        // 否则消息会先消失一拍再出现。
        setState(convId, (p) => ({
          ...p,
          busy: false,
          reloadTick: p.reloadTick + 1,
        }));
        emitConversationChanged();
        return;
      }
      if (e.type === "tool_call_end" && e.data.tool === "save_workflow") {
        const payload = parseToolPreview(e.data.preview);
        if (e.data.outcome === "success" && payload && !payload.error) {
          window.dispatchEvent(new CustomEvent(EVENT_WORKFLOWS_CHANGED));
          setState(convId, (p) => ({ ...p, toast: "已保存为工作流" }));
          window.setTimeout(() => {
            setState(convId, (p) => (
              p.toast === "已保存为工作流" ? { ...p, toast: null } : p
            ));
          }, TOAST_MS);
        }
      }
      if (e.type === "tool_call_end" && e.data.tool === "update_land_ruleset") {
        // The tool only stages an edit; surface it so the UI can open the admin
        // confirmation dialog (the write happens only on explicit confirm).
        const payload = parseToolPreview(e.data.preview);
        if (e.data.outcome === "success" && payload?.status === "pending_confirmation") {
          window.dispatchEvent(new CustomEvent(EVENT_PENDING_RULESET_EDIT, { detail: payload }));
        }
      }
      setState(convId, (p) => {
        if (!p.inflight) return p;
        const turn = p.inflight;
        switch (e.type) {
          case "reasoning_token":
            return { ...p, inflight: { ...turn, reasoning: turn.reasoning + e.data.delta } };
          case "assistant_token":
            return { ...p, inflight: { ...turn, tokens: turn.tokens + e.data.delta } };
          case "tool_call_start":
            return {
              ...p,
              inflight: {
                ...turn,
                toolCalls: [
                  ...turn.toolCalls,
                  {
                    callId: e.data.call_id,
                    tool: e.data.tool,
                    args: e.data.args,
                    outcome: "running",
                    errorCode: null,
                    durationMs: null,
                    preview: null,
                  },
                ],
              },
            };
          case "tool_call_end":
            return {
              ...p,
              inflight: {
                ...turn,
                toolCalls: turn.toolCalls.map((tc) =>
                  tc.callId === e.data.call_id
                    ? {
                        ...tc,
                        outcome: e.data.outcome,
                        errorCode: e.data.error_code,
                        durationMs: e.data.duration_ms,
                        preview: e.data.preview,
                      }
                    : tc,
                ),
              },
            };
          case "error":
            return { ...p, inflight: { ...turn, done: true, error: e.data } };
          default:
            return p;
        }
      });
    }, ctrl.signal, options);
    // stream ended cleanly; if message_done never fired (e.g. server hung up
    // early), still flip busy off and reload history.
    if (!messageDoneSeen) {
      setState(convId, (p) => ({
        ...p,
        busy: false,
        reloadTick: p.reloadTick + 1,
        }));
      emitConversationChanged();
    }
  } catch (err) {
    // User-initiated abort — clean exit, no error bubble. Bump reloadTick so
    // any partial assistant message that did land in the DB shows up.
    if ((err as Error)?.name === "AbortError" || ctrl.signal.aborted) {
      setState(convId, (p) => ({
        ...p,
        busy: false,
        reloadTick: p.reloadTick + 1,
        }));
      emitConversationChanged();
    } else {
      const msg = err instanceof ApiError ? err.message : String(err);
      const code = err instanceof ApiError ? err.code : "network";
      setState(convId, (p) => ({
        ...p,
        busy: false,
        inflight: p.inflight
          ? { ...p.inflight, done: true, error: { code, message: msg } }
          : { ...EMPTY_TURN, done: true, error: { code, message: msg } },
        // Don't bump reloadTick — the error bubble in inflight is the user's
        // signal; reloading would mask the failure.
      }));
      emitConversationChanged();
    }
  } finally {
    if (abortControllers.get(convId) === ctrl) {
      abortControllers.delete(convId);
    }
  }
}
